import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, API_CONFIG } from '../utils/api';
import './TaskView.css';

const ExifSection = ({ icon, title, rows, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const visibleRows = rows.filter(r => r.value != null && r.value !== '' && r.value !== 'undefined');
  if (visibleRows.length === 0) return null;

  return (
    <div className="exif-section">
      <button className="exif-section-header" onClick={() => setOpen(o => !o)}>
        <span><span className="exif-icon">{icon}</span>{title}</span>
        <span className={`exif-chevron ${open ? 'open' : ''}`}>&#9660;</span>
      </button>
      <div className={`exif-section-body ${open ? 'open' : ''}`}>
        {visibleRows.map((row, i) => (
          <div className="exif-row" key={i}>
            <span className="exif-label">{row.label}</span>
            <span className="exif-value">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TaskView = ({ propertyAddress, taskType, taskDate }) => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalImageLoaded, setModalImageLoaded] = useState(false);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${API_CONFIG.UPLOAD_BASE}/images`, {
        params: {
          property_address: propertyAddress,
          task_type: taskType,
          task_date: taskDate,
        },
      });
      setImages(response.data.images || response.data || []);
    } catch (err) {
      setError(err.message || t('taskView.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [propertyAddress, taskType, taskDate, t]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // Close modal on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const getThumbnailUrl = (image) => {
    if (image.thumbnail_url) {
      return image.thumbnail_url.startsWith('http')
        ? image.thumbnail_url
        : `${API_CONFIG.BASE_URL}${image.thumbnail_url}`;
    }
    // Fallback: extract filename from thumbnail_path
    const path = image.thumbnail_path || '';
    const filename = path.split('/').pop();
    return `${API_CONFIG.UPLOAD_BASE}/thumbnails/${filename}`;
  };

  const getOriginalUrl = (image) => {
    return `${API_CONFIG.UPLOAD_BASE}/originals/${image.image_id}`;
  };

  const handleImageLoad = (e) => {
    e.target.classList.add('loaded');
  };

  const parseExif = (image) => {
    if (!image.exif_data) return null;
    try {
      return typeof image.exif_data === 'string' ? JSON.parse(image.exif_data) : image.exif_data;
    } catch {
      return null;
    }
  };

  const parseDeviceInfo = (image) => {
    if (!image.device_info) return null;
    try {
      return typeof image.device_info === 'string' ? JSON.parse(image.device_info) : image.device_info;
    } catch {
      return null;
    }
  };

  const formatGps = (lat, lng) => {
    if (lat == null || lng == null) return null;
    return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
  };

  const formatGpsTimestamp = (ts, datestamp) => {
    if (!ts) return null;
    const parts = Array.isArray(ts) ? ts.map(v => String(Math.floor(v)).padStart(2, '0')).join(':') : String(ts);
    return datestamp ? `${datestamp} ${parts}` : parts;
  };

  const formatExifDateTime = (exifStr, tz) => {
    if (!exifStr) return null;
    // EXIF format: "YYYY:MM:DD HH:MM:SS" → convert to ISO for Date parsing
    const isoStr = exifStr.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return exifStr;
      return d.toLocaleString(undefined, {
        timeZone: tz || undefined,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      }) + (tz ? ` (${tz})` : '');
    } catch {
      return exifStr;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return null;
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const renderModal = () => {
    if (!selectedImage) return null;
    const exif = parseExif(selectedImage);
    const device = parseDeviceInfo(selectedImage);
    const tz = device?.timezone || null;

    // Camera info (nested under exif.camera)
    const cam = exif?.camera || {};
    const cameraRows = [
      { label: t('taskView.exif.make'), value: cam.make },
      { label: t('taskView.exif.model'), value: cam.model },
      { label: t('taskView.exif.lensModel'), value: cam.lens },
      { label: t('taskView.exif.software'), value: cam.software },
    ];

    // Settings (nested under exif.settings)
    const settings = exif?.settings || {};
    const settingsRows = [
      { label: t('taskView.exif.aperture'), value: settings.f_number ? `f/${settings.f_number}` : null },
      { label: t('taskView.exif.exposure'), value: settings.exposure_time },
      { label: t('taskView.exif.iso'), value: settings.iso },
      { label: t('taskView.exif.focalLength'), value: settings.focal_length ? `${settings.focal_length} mm` : null },
      { label: t('taskView.exif.flash'), value: settings.flash != null ? (settings.flash ? 'On' : 'Off') : null },
      { label: t('taskView.exif.orientation'), value: settings.orientation },
      { label: t('taskView.exif.imageWidth'), value: settings.image_width },
      { label: t('taskView.exif.imageHeight'), value: settings.image_height },
    ];

    // GPS (nested under exif.gps + record-level location)
    const gps = exif?.gps || {};
    const gpsLat = gps.latitude ?? selectedImage.location_lat;
    const gpsLng = gps.longitude ?? selectedImage.location_lng;
    const gpsCoords = formatGps(gpsLat, gpsLng);
    const gpsRows = [
      {
        label: t('taskView.exif.coordinates'),
        value: gpsCoords ? (
          <a href={`https://www.google.com/maps?q=${gpsLat},${gpsLng}`} target="_blank" rel="noopener noreferrer">
            {gpsCoords}
          </a>
        ) : null,
      },
      { label: t('taskView.exif.altitude'), value: gps.altitude != null ? `${Number(gps.altitude).toFixed(1)} m` : null },
      { label: t('taskView.exif.gpsTimestamp'), value: formatGpsTimestamp(gps.timestamp, gps.datestamp) },
      { label: t('taskView.exif.locationAccuracy'), value: selectedImage.location_accuracy != null ? `${Number(selectedImage.location_accuracy).toFixed(1)} m` : null },
    ];

    // Timestamps (nested under exif.timestamp), formatted in local timezone
    const ts = exif?.timestamp || {};
    const timeRows = [
      { label: t('taskView.exif.dateTimeOriginal'), value: formatExifDateTime(ts.date_time_original, tz) },
      { label: t('taskView.exif.dateTimeDigitized'), value: formatExifDateTime(ts.date_time_digitized, tz) },
      { label: t('taskView.exif.dateTime'), value: formatExifDateTime(ts.date_time, tz) },
      { label: t('taskView.exif.uploadTime'), value: formatExifDateTime(selectedImage.upload_timestamp, tz) },
      { label: t('taskView.exif.createdAt'), value: formatExifDateTime(selectedImage.created_at, tz) },
    ];

    // Device info
    const deviceRows = [
      { label: t('taskView.exif.platform'), value: device?.platform },
      { label: t('taskView.exif.browser'), value: device?.userAgent ? device.userAgent.split('(')[0].trim() : null },
      { label: t('taskView.exif.language'), value: device?.language },
      { label: t('taskView.exif.screen'), value: device?.screenWidth ? `${device.screenWidth}x${device.screenHeight} @${device.pixelRatio}x` : null },
      { label: t('taskView.exif.timezone'), value: device?.timezone },
    ];

    // File info
    const fileRows = [
      { label: t('taskView.exif.filename'), value: selectedImage.original_filename },
      { label: t('taskView.exif.fileSize'), value: formatFileSize(selectedImage.file_size) },
      { label: t('taskView.exif.fileType'), value: selectedImage.mime_type },
    ];

    return (
      <div className="task-view-modal-overlay" onClick={() => setSelectedImage(null)}>
        <div className="task-view-modal" onClick={(e) => e.stopPropagation()}>
          <div className="task-view-modal-close">
            <button onClick={() => setSelectedImage(null)} aria-label={t('taskView.close')}>✕</button>
          </div>
          <div className="task-view-modal-image">
            {!modalImageLoaded && (
              <div className="modal-image-loading">
                <div className="spinner"></div>
                <p>{t('taskView.loadingImage')}</p>
              </div>
            )}
            <img
              src={getOriginalUrl(selectedImage)}
              alt={selectedImage.original_filename || selectedImage.filename}
              onLoad={() => setModalImageLoaded(true)}
              style={{ display: modalImageLoaded ? 'block' : 'none' }}
            />
          </div>
          <div className="task-view-exif">
            <ExifSection icon="📷" title={t('taskView.exif.cameraSection')} rows={cameraRows} />
            <ExifSection icon="⚙️" title={t('taskView.exif.settingsSection')} rows={settingsRows} />
            <ExifSection icon="📍" title={t('taskView.exif.gpsSection')} rows={gpsRows} />
            <ExifSection icon="🕐" title={t('taskView.exif.timestampSection')} rows={timeRows} />
            <ExifSection icon="📱" title={t('taskView.exif.deviceSection')} rows={deviceRows} />
            <ExifSection icon="📄" title={t('taskView.exif.fileSection')} rows={fileRows} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="task-view-container">
        <div className="task-view-loading">
          <div className="spinner"></div>
          <p>{t('taskView.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="task-view-container">
        <div className="task-view-error">
          <h3>{t('taskView.errorTitle')}</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="task-view-container">
      <div className="task-view-header">
        <h2>{propertyAddress}</h2>
        <div className="task-view-meta">
          <span className="task-view-meta-item">
            <span className="meta-icon">📋</span> {taskType}
          </span>
          <span className="task-view-meta-item">
            <span className="meta-icon">📅</span> {taskDate}
          </span>
          <span className="task-view-meta-item">
            <span className="meta-icon">🖼️</span> {t('taskView.imageCount', { count: images.length })}
          </span>
        </div>
      </div>

      {images.length === 0 ? (
        <div className="task-view-empty">
          {t('taskView.noImages')}
        </div>
      ) : (
        <div className="task-view-grid">
          {images.map((image, index) => (
            <div
              className="task-view-thumb"
              key={image.id || index}
              onClick={() => { setModalImageLoaded(false); setSelectedImage(image); }}
            >
              <img
                src={getThumbnailUrl(image)}
                alt={`${index + 1}`}
                onLoad={handleImageLoad}
                loading="lazy"
              />
              <div className="task-view-thumb-overlay">
                #{index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {renderModal()}
    </div>
  );
};

export default TaskView;
